import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';

import reviewsData, { GOOGLE_RATING, GOOGLE_TOTAL_REVIEWS, GOOGLE_REVIEW_URL } from '../../constants/reviews';
import './GoogleReviews.css';

const AVATAR_COLORS = ['#7B1FA2', '#00796B', '#455A64', '#C2185B', '#5D4037', '#00838F', '#6A1B9A', '#2E7D32', '#AD1457', '#4527A0'];

const Stars = ({ value, size }) => (
  <span className={`greviews__stars ${size === 'lg' ? 'greviews__stars--lg' : ''}`} aria-label={`${value} / 5`}>
    <span className="greviews__stars-empty">★★★★★</span>
    <span className="greviews__stars-fill" style={{ width: `${(value / 5) * 100}%` }}>★★★★★</span>
  </span>
);

const Avatar = ({ name, index }) => (
  <span className="greviews__avatar" style={{ background: AVATAR_COLORS[index % AVATAR_COLORS.length] }}>
    {name.charAt(0).toUpperCase()}
  </span>
);

const GoogleReviews = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language && i18n.language.startsWith('en') ? 'en' : 'it';
  const trackRef = React.useRef(null);
  const [expanded, setExpanded] = React.useState(null);

  /* Paging per indice di card (non per pixel): le frecce restano sempre
     allineate allo snap, senza accumulare errori di arrotondamento */
  const scrollByPage = (dir) => {
    const track = trackRef.current;
    if (!track || !track.children.length) return;
    const cards = Array.from(track.children);
    const first = cards[0].offsetLeft;
    const stride = cards.length > 1 ? cards[1].offsetLeft - first : cards[0].offsetWidth;
    const perView = Math.max(1, Math.round(track.clientWidth / stride));
    const current = Math.round(track.scrollLeft / stride);
    const target = Math.min(cards.length - 1, Math.max(0, current + dir * perView));
    track.scrollTo({ left: cards[target].offsetLeft - first, behavior: 'smooth' });
  };

  React.useEffect(() => {
    if (expanded === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setExpanded(null);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [expanded]);

  const expandedReview = expanded !== null ? reviewsData[expanded] : null;

  return (
    <div className="greviews">
      <div className="greviews__header">
        <div className="greviews__brand">
          <span className="greviews__logo">
            Google <span className="greviews__logo-word">Reviews</span>
          </span>
          <span className="greviews__rating">
            <strong>{GOOGLE_RATING}</strong>
            <Stars value={GOOGLE_RATING} size="lg" />
            <span className="greviews__count">({GOOGLE_TOTAL_REVIEWS})</span>
          </span>
        </div>
        <a className="greviews__cta" href={GOOGLE_REVIEW_URL} target="_blank" rel="noreferrer">
          {t('review_us')}
        </a>
      </div>

      <div className="greviews__carousel">
        <button type="button" className="greviews__arrow greviews__arrow--prev" aria-label="Previous" onClick={() => scrollByPage(-1)}>
          <FiChevronLeft />
        </button>

        <ul className="greviews__track" ref={trackRef}>
          {reviewsData.map((review, index) => (
            <li className="greviews__card" key={review.id}>
              <div className="greviews__card-head">
                <Avatar name={review.name} index={index} />
                <div>
                  <p className="greviews__name">{review.name}</p>
                  <p className="greviews__date">
                    {review.timeAgo[lang]} {lang === 'en' ? 'on' : 'su'} <span className="greviews__g">Google</span>
                  </p>
                </div>
              </div>
              <Stars value={review.rating} />
              <p className="greviews__text">{review.text}</p>
              <button type="button" className="greviews__more" onClick={() => setExpanded(index)}>
                {t('read_more')}
              </button>
            </li>
          ))}
        </ul>

        <button type="button" className="greviews__arrow greviews__arrow--next" aria-label="Next" onClick={() => scrollByPage(1)}>
          <FiChevronRight />
        </button>
      </div>

      {expandedReview && (
        <div className="greviews__overlay" role="dialog" aria-modal="true" onClick={() => setExpanded(null)}>
          <div className="greviews__modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="greviews__close" aria-label="Close" onClick={() => setExpanded(null)}>
              <FiX />
            </button>
            <div className="greviews__card-head">
              <Avatar name={expandedReview.name} index={expanded} />
              <div>
                <p className="greviews__name">{expandedReview.name}</p>
                <p className="greviews__date">
                  {expandedReview.timeAgo[lang]} {lang === 'en' ? 'on' : 'su'} <span className="greviews__g">Google</span>
                </p>
              </div>
            </div>
            <Stars value={expandedReview.rating} size="lg" />
            <p className="greviews__modal-text">{expandedReview.text}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleReviews;
